import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Coins, Loader2 } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtNumber } from "@/i18n/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function useCatalog() {
  const [c, setC] = useState(null);
  const load = () => api.get("/billing/catalog").then((r) => setC(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return [c, load];
}

export function PackGrid({ catalog, returnPath = "/app/credits" }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(null);
  if (!catalog) return <div className="h-40 rounded-xl bg-muted shimmer" />;
  const buy = async (id) => {
    setBusy(id);
    try { const { data } = await api.post("/billing/credit-packs/checkout", { pack: id, origin_url: window.location.origin, return_path: returnPath }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errorDetail(e)); setBusy(null); }
  };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="credit-packs">
      {catalog.packs.map((p, i) => (
        <div key={p.id} className={`card-surface p-4 flex flex-col transition-transform duration-200 hover:-translate-y-0.5 ${i === 2 ? "ring-2 ring-primary/40" : ""}`} data-testid={`pack-${p.id}`}>
          <div className="eyebrow">{p.id.charAt(0).toUpperCase() + p.id.slice(1)}</div>
          <div className="text-2xl font-extrabold font-mono mt-2">{fmtNumber(p.credits)}</div>
          <div className="text-xs text-muted-foreground">{t("AI credits")}</div>
          <div className="mt-3 font-semibold" data-testid={`pack-price-${p.id}`}>{fmtMoney(p.price, catalog.currency)} <span className="text-xs text-muted-foreground font-normal">{t("excl. tax")}</span></div>
          <div className="text-[11px] text-muted-foreground mt-1">{t("Valid {{count}} months", { count: 12 })}</div>
          <Button size="sm" className="mt-4" disabled={!!busy} onClick={() => buy(p.id)} data-testid={`buy-pack-${p.id}`}>{busy === p.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{t("Buy")}</Button>
        </div>
      ))}
    </div>
  );
}

export function InsufficientCreditsModal() {
  const { t } = useI18n();
  const { user, setUpgradeOpen } = useAuth();
  const paid = !!user?.entitlements?.is_paid;
  const loc = useLocation();
  const [info, setInfo] = useState(null);
  const [showPacks, setShowPacks] = useState(false);
  const [catalog] = useCatalog();
  useEffect(() => {
    // Packs are for subscribers only: trial / unpaid organizations are offered the base subscription first.
    const h = (e) => { if (!paid) { setUpgradeOpen(true); return; } setInfo(e.detail); setShowPacks(false); };
    window.addEventListener("tt:insufficient-credits", h);
    return () => window.removeEventListener("tt:insufficient-credits", h);
  }, [paid, setUpgradeOpen]);
  return (
    <Dialog open={!!info} onOpenChange={(o) => !o && setInfo(null)}>
      <DialogContent className={showPacks ? "max-w-5xl" : "max-w-md"} data-testid="insufficient-credits-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Coins className="w-5 h-5 text-primary" />{t("Insufficient AI credits")}</DialogTitle>
          <DialogDescription>{t("You need additional AI credits to complete this operation.")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="card-surface p-3"><div className="text-xs text-muted-foreground">{t("Available credits")}</div><div className="font-mono text-xl font-bold" data-testid="ic-available">{fmtNumber(info?.available ?? 0)}</div></div>
          <div className="card-surface p-3"><div className="text-xs text-muted-foreground">{t("Required credits")}</div><div className="font-mono text-xl font-bold" data-testid="ic-required">{fmtNumber(info?.required ?? 0)}</div></div>
        </div>
        <p className="text-sm text-muted-foreground">{t("Choose a credit package to continue.")}</p>
        {showPacks && <PackGrid catalog={catalog} returnPath={loc.pathname} />}
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="ghost" onClick={() => setInfo(null)} data-testid="ic-cancel">{t("Cancel")}</Button>
          <Button variant="outline" onClick={() => setShowPacks(true)} data-testid="ic-view-packs">{t("View packages")}</Button>
          <Button onClick={() => setShowPacks(true)} data-testid="ic-buy">{t("Buy AI credits")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
